import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  PencilIcon, 
  TrashIcon, 
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CubeIcon,
  ScaleIcon,
  QrCodeIcon,
  PlusIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { catalogApi, proposalsApi } from '../../services/api';
import type { Service, ServiceFeature, ServiceImage, ServiceItem } from '../../types';

const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [service, setService] = useState<Service | null>(null);
  const [features, setFeatures] = useState<ServiceFeature[]>([]);
  const [images, setImages] = useState<ServiceImage[]>([]);
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [newItemQuantity, setNewItemQuantity] = useState(1);

  useEffect(() => {
    if (id) {
      loadServiceData();
    }
  }, [id]);

  const loadServiceData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [serviceData, featuresData, imagesData] = await Promise.all([
        catalogApi.getService(id),
        catalogApi.getServiceFeatures({ service: id, ordering: 'order,name' }),
        catalogApi.getServiceImages({ service: id, ordering: 'order,created_at' })
      ]);

      setService(serviceData);
      setFeatures(featuresData);
      setImages(imagesData);
      setError(null);

      // Se o equipamento rastreia itens individuais, carrega os itens
      if (serviceData.track_individual_items) {
        loadServiceItems();
      }
    } catch (err) {
      setError('Erro ao carregar detalhes do equipamento');
      console.error('Error loading service data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Funções de gestão de inventário
  const loadServiceItems = async () => {
    if (!id) return;

    try {
      setItemsLoading(true);
      const itemsData = await proposalsApi.getServiceItems({ service: id });
      setItems(itemsData.results || []);
    } catch (err) {
      console.error('Error loading service items:', err);
    } finally {
      setItemsLoading(false);
    }
  };

  const handleCreateItems = async () => {
    if (!service || !id) return;

    try {
      setItemsLoading(true);
      await proposalsApi.bulkCreateServiceItems({
        service: id,
        quantity: newItemQuantity
      });
      
      alert(`✅ ${newItemQuantity} item(s) criado(s) com sucesso!`);
      setNewItemQuantity(1);
      setShowItemForm(false);
      
      // Recarregar items e service para atualizar contadores
      await Promise.all([
        loadServiceItems(),
        loadServiceData()
      ]);
    } catch (error: any) {
      console.error('Error creating items:', error);
      alert(`❌ ${error.response?.data?.error || 'Erro ao criar itens'}`);
    } finally {
      setItemsLoading(false);
    }
  };

  const handleChangeItemStatus = async (itemId: string, newStatus: string) => {
    try {
      setItemsLoading(true);
      await proposalsApi.changeServiceItemStatus(itemId, newStatus);
      
      alert(`✅ Status alterado com sucesso!`);
      await loadServiceItems();
    } catch (error: any) {
      console.error('Error changing item status:', error);
      alert(`❌ ${error.response?.data?.error || 'Erro ao alterar status'}`);
    } finally {
      setItemsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800 border-green-200';
      case 'rented': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'damaged': return 'bg-red-100 text-red-800 border-red-200';
      case 'retired': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <CheckCircleIcon className="h-4 w-4" />;
      case 'rented': return <CubeIcon className="h-4 w-4" />;
      case 'maintenance': return <WrenchScrewdriverIcon className="h-4 w-4" />;
      case 'damaged': return <ExclamationCircleIcon className="h-4 w-4" />;
      case 'retired': return <XCircleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const handleToggleFeatured = async () => {
    if (!service) return;

    try {
      setActionLoading(true);
      const result = await catalogApi.toggleServiceFeatured(service.id.toString());
      setService(prev => prev ? { ...prev, featured: result.featured } : null);
    } catch (err) {
      console.error('Error toggling featured:', err);
      alert('Erro ao alterar destaque do equipamento');
    } finally {
      setActionLoading(false);
    }
  };

  const handleChangeAvailability = async (status: string) => {
    if (!service) return;

    try {
      setActionLoading(true);
      const updatedService = await catalogApi.changeServiceAvailability(service.id.toString(), status);
      setService(updatedService);
    } catch (err) {
      console.error('Error changing availability:', err);
      alert('Erro ao alterar disponibilidade do equipamento');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!service) return;

    if (window.confirm('Tem certeza que deseja excluir este equipamento? Esta ação não pode ser desfeita.')) {
      try {
        setActionLoading(true);
        await catalogApi.deleteService(service.id.toString());
        navigate('/catalog');
      } catch (err) {
        console.error('Error deleting service:', err);
        alert('Erro ao excluir equipamento');
        setActionLoading(false);
      }
    }
  };

  const getAvailabilityConfig = (status: string) => {
    switch (status) {
      case 'available':
        return {
          icon: CheckCircleIcon,
          text: 'Disponível',
          color: 'text-green-600',
          bg: 'bg-green-100',
          border: 'border-green-200'
        };
      case 'unavailable':
        return {
          icon: XCircleIcon,
          text: 'Indisponível',
          color: 'text-red-600',
          bg: 'bg-red-100',
          border: 'border-red-200'
        };
      case 'maintenance':
        return {
          icon: ExclamationCircleIcon,
          text: 'Em Manutenção',
          color: 'text-yellow-600',
          bg: 'bg-yellow-100',
          border: 'border-yellow-200'
        };
      case 'rented':
        return {
          icon: ClockIcon,
          text: 'Alugado',
          color: 'text-blue-600',
          bg: 'bg-blue-100',
          border: 'border-blue-200'
        };
      default:
        return {
          icon: XCircleIcon,
          text: status,
          color: 'text-gray-600',
          bg: 'bg-gray-100',
          border: 'border-gray-200'
        };
    }
  };

  const formatPrice = (price: number | string | null | undefined) => {
    if (!price) return '-';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numPrice);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando detalhes do equipamento...</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error || 'Equipamento não encontrado'}</p>
          <button
            onClick={() => navigate('/catalog')}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Voltar para o catálogo
          </button>
        </div>
      </div>
    );
  }

  const availabilityConfig = getAvailabilityConfig(service.availability_status);
  const AvailabilityIcon = availabilityConfig.icon;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg mb-1">
        <div className="px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-sm font-bold text-gray-800">{service.name}</h1>
            {service.featured && (
              <StarIconSolid className="h-6 w-6 text-yellow-500" />
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleToggleFeatured}
              disabled={actionLoading}
              className={`p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                service.featured ? 'text-yellow-500' : 'text-gray-400'
              }`}
              title={service.featured ? 'Remover destaque' : 'Destacar equipamento'}
            >
              <StarIcon className="h-4 w-4" />
            </button>
            
            <Link
              to={`/catalog/services/${service.id}/edit`}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Editar equipamento"
            >
              <PencilIcon className="h-4 w-4" />
            </Link>
            
            <button
              onClick={handleDelete}
              disabled={actionLoading}
              className="p-2 text-red-600 hover:bg-red-50 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500"
              title="Excluir equipamento"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="px-3 py-1.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-600">Categoria:</span>
              <span className="text-blue-600 font-medium">{service.category.name}</span>
            </div>
            
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full border ${availabilityConfig.bg} ${availabilityConfig.border}`}>
              <AvailabilityIcon className={`h-4 w-4 ${availabilityConfig.color}`} />
              <span className={`text-sm font-medium ${availabilityConfig.color}`}>
                {availabilityConfig.text}
              </span>
            </div>
          </div>

          {service.short_description && (
            <p className="mt-3 text-xs text-gray-600">{service.short_description}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-1">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-1">
          {/* Description */}
          <div className="bg-white shadow-sm rounded-lg p-2">
            <h2 className="text-sm font-bold text-gray-800 mb-1">Descrição</h2>
            <div className="text-gray-700 whitespace-pre-wrap">
              {service.description}
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="bg-white shadow-sm rounded-lg p-2">
            <h2 className="text-sm font-bold text-gray-800 mb-1">Especificações Técnicas</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mb-1">
              {service.capacity && (
                <div className="flex items-center space-x-2">
                  <CubeIcon className="h-4 w-4 text-blue-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-600">Capacidade</div>
                    <div className="text-gray-800">{service.capacity}</div>
                  </div>
                </div>
              )}
              
              {service.dimensions && (
                <div className="flex items-center space-x-2">
                  <ScaleIcon className="h-4 w-4 text-blue-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-600">Dimensões</div>
                    <div className="text-gray-800">{service.dimensions}</div>
                  </div>
                </div>
              )}
              
              {service.weight && (
                <div className="flex items-center space-x-2">
                  <ScaleIcon className="h-4 w-4 text-blue-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-600">Peso</div>
                    <div className="text-gray-800">{service.weight}</div>
                  </div>
                </div>
              )}
            </div>

            {service.technical_specs && Object.keys(service.technical_specs).length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-gray-800 mb-1">Especificações Adicionais</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {Object.entries(service.technical_specs)
                    .filter(([key]) => !['caracteristicas', 'service_type'].includes(key))
                    .map(([key, value]) => {
                      // Se for um objeto ou array, converter para string
                      const displayValue = typeof value === 'object'
                        ? JSON.stringify(value)
                        : String(value);

                      return (
                        <div key={key}>
                          <dt className="text-sm font-medium text-gray-600">{key}</dt>
                          <dd className="text-gray-800 text-xs">{displayValue}</dd>
                        </div>
                      );
                    })}
                </dl>
              </div>
            )}
          </div>

          {/* Características from technical_specs */}
          {service.technical_specs?.caracteristicas && Array.isArray(service.technical_specs.caracteristicas) && service.technical_specs.caracteristicas.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg p-2">
              <h2 className="text-sm font-bold text-gray-800 mb-1">Características do Equipamento</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {service.technical_specs.caracteristicas.map((caracteristica: string, index: number) => (
                  <div key={index} className="flex items-start space-x-2">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    </div>
                    <div className="text-sm text-gray-700">{caracteristica}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Features */}
          {features.length > 0 && (
            <div className="bg-white shadow-sm rounded-lg p-2">
              <h2 className="text-sm font-bold text-gray-800 mb-1">Características</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {features.map((feature) => (
                  <div key={feature.id} className="flex items-start space-x-2">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">{feature.name}</div>
                      {feature.description && (
                        <div className="text-sm text-gray-600">{feature.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-1">
          {/* Pricing */}
          <div className="bg-white shadow-sm rounded-lg p-2">
            <h2 className="text-sm font-bold text-gray-800 mb-1 flex items-center">
              <CurrencyDollarIcon className="h-4 w-4 mr-1.5" />
              Preços
            </h2>
            
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Diário:</span>
                <span className="font-medium text-gray-800">{formatPrice(service.daily_price)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Semanal:</span>
                <span className="font-medium text-gray-800">{formatPrice(service.weekly_price)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Mensal:</span>
                <span className="font-medium text-gray-800">{formatPrice(service.monthly_price)}</span>
              </div>
            </div>
          </div>

          {/* Inventory Management */}
          <div className="bg-white shadow-sm rounded-lg p-2">
            <h2 className="text-sm font-bold text-gray-800 mb-1 flex items-center">
              <Squares2X2Icon className="h-4 w-4 mr-1.5" />
              Inventário
            </h2>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Quantidade Total:</span>
                <span className="font-medium text-gray-800">{service.total_quantity || 0}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Disponível:</span>
                <span className="font-medium text-green-600">{service.available_quantity || 0}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Alugado:</span>
                <span className="font-medium text-blue-600">{service.rented_quantity || 0}</span>
              </div>
              
              <div className="flex items-center pt-2">
                <input
                  type="checkbox"
                  id="trackItems"
                  checked={service.track_individual_items || false}
                  readOnly
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="trackItems" className="ml-2 text-sm text-gray-600">
                  Rastreamento Individual com QR
                </label>
              </div>
              
              {service.track_individual_items && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button 
                    className="w-full flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => alert('Funcionalidade de gestão de itens individuais em desenvolvimento')}
                  >
                    <QrCodeIcon className="h-4 w-4 mr-1.5" />
                    Gerenciar Itens QR
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white shadow-sm rounded-lg p-2">
            <h2 className="text-sm font-bold text-gray-800 mb-1">Ações</h2>
            
            <div className="space-y-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alterar Disponibilidade
                </label>
                <select
                  value={service.availability_status}
                  onChange={(e) => handleChangeAvailability(e.target.value)}
                  disabled={actionLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="available">Disponível</option>
                  <option value="unavailable">Indisponível</option>
                  <option value="maintenance">Em Manutenção</option>
                  <option value="rented">Alugado</option>
                </select>
              </div>

              <Link
                to={`/catalog/services/${service.id}/edit`}
                className="w-full flex items-center justify-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="h-4 w-4 mr-1.5" />
                Editar Equipamento
              </Link>
            </div>
          </div>

          {/* Service Info */}
          <div className="bg-white shadow-sm rounded-lg p-2">
            <h2 className="text-sm font-bold text-gray-800 mb-1">Informações</h2>
            
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">ID:</span>
                <span className="text-gray-800">#{service.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Criado em:</span>
                <span className="text-gray-800">
                  {new Date(service.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Atualizado em:</span>
                <span className="text-gray-800">
                  {new Date(service.updated_at).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={service.is_active ? 'text-green-600' : 'text-red-600'}>
                  {service.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </div>
            </div>
          </div>

          {/* Inventory Management - Only for services that track individual items */}
          {service.track_individual_items && (
            <div className="bg-white shadow-sm rounded-lg p-2">
              <div className="flex items-center justify-between mb-1">
                <h2 className="flex items-center text-sm font-bold text-gray-800">
                  <Squares2X2Icon className="h-4 w-4 mr-1.5" />
                  Gestão de Inventário
                </h2>
                <button
                  onClick={() => setShowItemForm(!showItemForm)}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 transition-colors flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Adicionar Itens
                </button>
              </div>

              {/* Inventory Summary */}
              <div className="grid grid-cols-3 gap-4 mb-3 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{service.total_quantity}</div>
                  <div className="text-xs text-gray-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{service.available_quantity}</div>
                  <div className="text-xs text-gray-600">Disponível</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{service.rented_quantity}</div>
                  <div className="text-xs text-gray-600">Alugado</div>
                </div>
              </div>

              {/* Add Items Form */}
              {showItemForm && (
                <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-blue-50">
                  <h3 className="text-sm font-medium text-gray-800 mb-2">Criar Novos Itens</h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={newItemQuantity}
                      onChange={(e) => setNewItemQuantity(parseInt(e.target.value) || 1)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Qtd"
                    />
                    <span className="text-sm text-gray-600">item(s) com QR Code automático</span>
                    <button
                      onClick={handleCreateItems}
                      disabled={itemsLoading}
                      className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700 disabled:opacity-50"
                    >
                      {itemsLoading ? 'Criando...' : 'Criar'}
                    </button>
                    <button
                      onClick={() => setShowItemForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <XCircleIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Items List */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {itemsLoading ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    Carregando itens...
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500">
                    Nenhum item cadastrado
                  </div>
                ) : (
                  items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center">
                          {getStatusIcon(item.status)}
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                            {item.status_display}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-sm text-gray-800">{item.internal_code}</div>
                          <div className="flex items-center text-xs text-gray-500">
                            <QrCodeIcon className="h-3 w-3 mr-1" />
                            {item.qr_code}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <select
                          value={item.status}
                          onChange={(e) => handleChangeItemStatus(item.id, e.target.value)}
                          className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="available">Disponível</option>
                          <option value="rented">Alugado</option>
                          <option value="maintenance">Manutenção</option>
                          <option value="damaged">Danificado</option>
                          <option value="retired">Aposentado</option>
                        </select>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Back to catalog */}
      <div className="mt-2 text-center">
        <Link
          to="/catalog"
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          ← Voltar ao Catálogo
        </Link>
      </div>
    </div>
  );
};

export default ServiceDetail;